import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IndianRupee, ReceiptText } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { usePaymentDeskRefData } from "./shared/usePaymentDeskRefData";

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

const MODE_OPTIONS = [
  { value: "all", label: "All Modes" },
  { value: "rtgs", label: "RTGS/NEFT" },
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "imprest", label: "Imprest Bills" },
];

const VIEWS = [
  { key: "site", label: "Site-wise", dataKey: "by_site", empty: "No site-attributed expenses found." },
  { key: "vendor", label: "Vendor-wise", dataKey: "by_vendor", empty: "No vendor expenses found." },
  { key: "equipment", label: "Equipment-wise", dataKey: "by_equipment", empty: "No equipment-attributed expenses found." },
];

// Payment Desk Phase 3 — expense tracking rollups across all three modes
// (RTGS/NEFT paid lines + Cash + UPI post-facto entries). One-bucket
// attribution (site OR equipment; "Head Office / Unattributed" for
// neither), drill-down opens a read-only paginated transaction list.
export default function ExpenseTrackerTab() {
  const refData = usePaymentDeskRefData();
  const [entityFilter, setEntityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("site");
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillPage, setDrillPage] = useState(1);
  const [drillTotalPages, setDrillTotalPages] = useState(1);

  const filterParams = () => {
    const params = { mode: modeFilter };
    if (entityFilter !== "all") params.entity_code = entityFilter;
    if (categoryFilter !== "all") params.category_id = categoryFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const load = () => {
    setLoading(true);
    client
      .get("/payment-desk/expenses/summary", { params: filterParams() })
      .then((res) => setSummary(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [entityFilter, categoryFilter, modeFilter, dateFrom, dateTo]);

  const loadDrill = (pageNum = 1) => {
    if (!drill) return;
    setDrillLoading(true);
    client
      .get("/payment-desk/expenses/transactions", {
        params: { ...filterParams(), group_by: drill.groupBy, key: drill.key, page: pageNum, page_size: 50 },
      })
      .then((res) => {
        setDrillRows(res.data.items);
        setDrillPage(res.data.page);
        setDrillTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setDrillLoading(false));
  };

  useEffect(() => {
    if (drill) loadDrill(1);
  }, [drill]);

  const openDrill = (groupBy, row) => setDrill({ groupBy, key: row.key, label: row.label });

  return (
    <div className="space-y-4" data-testid="expense-tracker-tab">
      <div className="flex flex-wrap items-center gap-3">
        <SearchableSelect testId="expense-entity-filter" className="w-48" value={entityFilter} onValueChange={setEntityFilter} placeholder="All Entities"
  options={[{ value: "all", label: "All Entities", pinned: true }, ...refData.entities.map((en) => ({ value: en.code, label: en.name }))]} />
        <SearchableSelect testId="expense-category-filter" className="w-52" value={categoryFilter} onValueChange={setCategoryFilter} placeholder="All Categories"
  options={[{ value: "all", label: "All Categories", pinned: true }, ...refData.categories.map((c) => ({ value: c.id, label: c.name }))]} />
        <Select value={modeFilter} onValueChange={setModeFilter}>
          <SelectTrigger data-testid="expense-mode-filter" className="w-40">
            <SelectValue placeholder="All Modes" />
          </SelectTrigger>
          <SelectContent>
            {MODE_OPTIONS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Input type="date" data-testid="expense-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" data-testid="expense-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="expense-grand-total-card">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
            <IndianRupee className="h-3.5 w-3.5" /> Total Spent
          </div>
          <div className="mt-1 text-2xl font-bold text-slate-900" data-testid="expense-grand-total">
            {loading ? "…" : money(summary?.grand_total)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="expense-txn-count-card">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
            <ReceiptText className="h-3.5 w-3.5" /> Transactions
          </div>
          <div className="mt-1 text-2xl font-bold text-slate-900" data-testid="expense-txn-count">
            {loading ? "…" : summary?.transaction_count ?? 0}
          </div>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          {VIEWS.map((v) => (
            <TabsTrigger key={v.key} value={v.key} data-testid={`expense-view-${v.key}`}>
              {v.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {VIEWS.map((v) => (
          <TabsContent key={v.key} value={v.key} className="pt-3">
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <Table data-testid={`expense-${v.key}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{v.key === "vendor" ? "Vendor" : v.key === "site" ? "Site" : "Equipment"}</TableHead>
                    <TableHead>Transactions</TableHead>
                    <TableHead>Total Spent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
                  ) : !summary || summary[v.dataKey].length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-400">{v.empty}</TableCell></TableRow>
                  ) : (
                    summary[v.dataKey].map((row) => (
                      <TableRow
                        key={row.key}
                        data-testid={`expense-${v.key}-row-${row.key}`}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => openDrill(v.key, row)}
                      >
                        <TableCell className="text-sm font-medium text-slate-800">{row.label}</TableCell>
                        <TableCell className="text-xs text-slate-500">{row.count}</TableCell>
                        <TableCell>{money(row.total)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {drill && (
        <Dialog open onOpenChange={(open) => !open && setDrill(null)}>
          <DialogContent className="max-w-5xl" data-testid="expense-drilldown-dialog">
            <DialogHeader>
              <DialogTitle>Transactions — {drill.label}</DialogTitle>
              <DialogDescription>All payments matching this bucket and the current filters, across RTGS/NEFT, Cash and UPI.</DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table data-testid="expense-txn-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Bill</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Attributed To</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillLoading ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
                  ) : drillRows.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">No transactions found.</TableCell></TableRow>
                  ) : (
                    drillRows.map((t) => (
                      <TableRow key={t.ref} data-testid={`expense-txn-row-${t.ref.replace(/[^A-Za-z0-9-]/g, "-")}`}>
                        <TableCell className="font-mono text-xs font-semibold">{t.ref}</TableCell>
                        <TableCell className="text-xs">{t.source}</TableCell>
                        <TableCell className="text-xs">{t.vendor_name || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {t.vendor_bill_id || "—"}
                          {t.bill_amount_applied != null && <span className="block text-slate-400">applied {money(t.bill_amount_applied)}</span>}
                        </TableCell>
                        <TableCell className="text-xs">{t.category_name}</TableCell>
                        <TableCell className="text-xs">{t.entity_name || t.entity_code}</TableCell>
                        <TableCell className="text-xs">{t.site_label || t.equipment_label || "—"}</TableCell>
                        <TableCell>{money(t.amount)}</TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {t.payment_date ? new Date(t.payment_date).toLocaleDateString("en-IN") : "—"}
                        </TableCell>
                        <TableCell className="text-xs">{t.status}</TableCell>
                        <TableCell className="text-xs text-slate-500">{t.entered_by_name || "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <PaginationBar page={drillPage} totalPages={drillTotalPages} onPageChange={loadDrill} testId="expense-txn-pagination" />
            <div className="flex justify-end">
              <Button variant="outline" data-testid="expense-drilldown-close" onClick={() => setDrill(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
